import { isNotFound } from '@tanstack/react-router';
import { Suspense } from 'react';
import { Skeleton } from '@worldcoo/ui/components/skeleton';
import { useTranslation } from '@/lib/i18n';
import { Card, CardHeader, CardTitle, CardContent } from '@worldcoo/ui/components/card';
import { DivWithPrimaryColorVariable } from './div-with-primary-color-variable';
import { ObjectivesIcon } from './assets/campaign-texts/objectives-icon';
import { DescriptionIcon } from './assets/campaign-texts/description-icon';
import { BeneficiariesIcon } from './assets/campaign-texts/beneficiaries-icon';
import { UpdatesAndLinksIcon } from './assets/campaign-texts/updates-and-links-icon';
import { usePortalCampaignQuery } from '@/lib/queries';
import { cn, heroImageSize } from '@worldcoo/ui/lib/utils';
import { sanitizeHTML } from '@worldcoo/ui/lib/sanitize-html';
import { ActivityIcon } from './assets/campaign-texts/activity-icon';

export function Campaign(props: { campaignId: string }) {
	return (
		<Suspense
			fallback={
				<div className="flex flex-col lg:flex-row">
					<section className="flex w-full flex-col gap-6 px-6 pb-10 lg:w-1/2">
						<Skeleton className="bg-secondary h-9 w-32" />

						<div className="flex flex-col gap-1">
							<Skeleton className="bg-secondary h-6 w-lg" />
							<Skeleton className="bg-secondary h-6 w-md" />
							<Skeleton className="bg-secondary h-6 w-lg" />
							<br />
							<Skeleton className="bg-secondary h-6 w-lg" />
							<Skeleton className="bg-secondary h-6 w-md" />
							<br />
							<Skeleton className="bg-secondary h-6 w-lg" />
							<Skeleton className="bg-secondary h-6 w-md" />
							<Skeleton className="bg-secondary h-6 w-sm" />
						</div>

						<Skeleton className="bg-secondary h-14 w-48" />
					</section>

					<div className="w-full p-10 pb-20 lg:w-1/2">
						<Skeleton className={cn('bg-secondary h-full w-full rounded-2xl', heroImageSize.className)} />
					</div>
				</div>
			}
		>
			<SuspensedCampaign campaignId={props.campaignId} />
		</Suspense>
	);
}

function SuspensedCampaign(props: { campaignId: string }) {
	const { i18n } = useTranslation();
	const campaign = usePortalCampaignQuery(props.campaignId);

	if (isNotFound(campaign)) return null;

	return (
		<DivWithPrimaryColorVariable className="flex w-full flex-col gap-6">
			{isNotEmpty(campaign.data.texts.name) && (
				<div
					className="text-primary text-3xl font-semibold"
					dangerouslySetInnerHTML={{
						__html: sanitizeHTML(campaign.data.texts.name),
					}}
				/>
			)}

			{isNotEmpty(campaign.data.texts.objectives) && (
				<Card className="shadow-4xl gap-0 border-none px-6 py-12">
					<CardHeader>
						<CardTitle className="flex items-center gap-2 text-2xl">
							<ObjectivesIcon className="text-primary" />
							{i18n.campaign.objectives}
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div
							className="unreset-tailwind"
							dangerouslySetInnerHTML={{
								__html: sanitizeHTML(campaign.data.texts.objectives),
							}}
						/>
					</CardContent>
				</Card>
			)}

			{isNotEmpty(campaign.data.texts.resume) && (
				<Card className="shadow-4xl gap-0 border-none px-6 py-12">
					<CardHeader>
						<CardTitle className="flex items-center gap-2 text-2xl">
							<DescriptionIcon className="text-primary" />
							{i18n.campaign.description}
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div
							className="unreset-tailwind"
							dangerouslySetInnerHTML={{
								__html: sanitizeHTML(campaign.data.texts.resume),
							}}
						/>
					</CardContent>
				</Card>
			)}

			{isNotEmpty(campaign.data.texts.activities) && (
				<Card className="shadow-4xl gap-0 border-none px-6 py-12">
					<CardHeader>
						<CardTitle className="flex items-center gap-2 text-2xl">
							<ActivityIcon className="text-primary" />
							{i18n.campaign.activities}
						</CardTitle>
					</CardHeader>
					<CardContent>
						{/* TODO: Make an accordion */}
						<div
							className="unreset-tailwind"
							dangerouslySetInnerHTML={{
								__html: sanitizeHTML(campaign.data.texts.activities),
							}}
						/>
					</CardContent>
				</Card>
			)}

			{isNotEmpty(campaign.data.texts.benefits) && (
				<Card className="shadow-4xl gap-0 border-none px-6 py-12">
					<CardHeader>
						<CardTitle className="flex items-center gap-2 text-2xl">
							<BeneficiariesIcon className="text-primary" />
							{i18n.campaign.beneficiaries}
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div
							className="unreset-tailwind"
							dangerouslySetInnerHTML={{
								__html: sanitizeHTML(campaign.data.texts.benefits),
							}}
						/>
					</CardContent>
				</Card>
			)}

			{isNotEmpty(campaign.data.texts.updateAndLinks) && (
				<Card className="shadow-4xl gap-0 border-none px-6 py-12">
					<CardHeader>
						<CardTitle className="flex items-center gap-2 text-2xl">
							<UpdatesAndLinksIcon className="text-primary" />
							{i18n.campaign.updatesAndLinks}
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div
							className="unreset-tailwind"
							dangerouslySetInnerHTML={{
								__html: sanitizeHTML(campaign.data.texts.updateAndLinks),
							}}
						/>
					</CardContent>
				</Card>
			)}
		</DivWithPrimaryColorVariable>
	);
}

function isNotEmpty(text: string | undefined): text is string {
	return text !== undefined && text !== '' && text !== '-' && text !== '<p>-</p>';
}
